import { ReactElement } from "react";
import type { GetStaticProps, GetStaticPaths } from "next";
import NextHeadSeo from "next-head-seo";

import LayoutMain from "~/components/layouts/LayoutMain";
import { Header } from "~/components/app/Header";
import {
  restApiGetPostBySlugOrFallbackId,
  restApiESGetSettings,
  restApiESQuery,
} from "~/utils/restApi";
import PageMargins from "~/components/styled/PageMargins";
import { Heading } from "~/components/ui/Heading";
import { appConfig } from "~/config";
import { WhatsOnListing } from "~/components/content/WhatsOnListing";
import { PageSubMenu } from "~/components/ui/PageSubMenu";
import { FlexibleRow } from "~/components/flexibleContent/FlexibleRow";
import VerticalSlider from "~/components/ui/VerticalSlider";
import { VerticalSliderItemWithTeaser } from "~/components/content/VerticalSliderItemWithTeaser";
import { Pagination } from "~/components/ui/Pagination";
import { useConfigContext } from "~/providers/ConfigContextProvider";
import SafeHtmlSpan from "~/components/ui/SafeHtmlSpan";
import { ListingInfo } from "~/components/ui/ListingInfo";
import { sortEventsByDateAndTime } from "~/utils/eventsHelper";

const WhatsOnCategoryList = ({
  upcoming,
  category,
  current,
  currentEvents,
  past,
  page,
  currentPage,
}: {
  category: string;
  current: any;
  upcoming: any;
  currentPage: any;
  past: any;
  currentEvents: any;
  page: number;
}) => {
  const config = useConfigContext();

  return (
    <>
      <NextHeadSeo
        canonical={currentPage?.yoast_head_json?.canonical}
        title={`${currentPage?.title} ${
          page > 1 ? `- ${page}` : ``
        } - What's On - ${config.appTitle}`}
        description={currentPage?.yoast_head_json?.description}
        og={{
          title: currentPage?.yoast_head_json?.og_title,
          type: currentPage?.yoast_head_json?.og_type,
          siteName: currentPage?.yoast_head_json?.og_site_name,
          image: currentPage?.yoast_head_json?.twitter_image,
        }}
        twitter={{
          card: "summary_large_image",
        }}
      />

      <Header>
        <PageSubMenu
          title={currentPage?.title}
          menu={currentPage?.subMenu?.menu}
        />
      </Header>

      {page === 1 ? (
        <>
          {currentPage?.acf?.content?.length > 0 && (
            <FlexibleRow field="content" content={currentPage.acf.content} />
          )}
        </>
      ) : (
        <ListingInfo>
          You are on page <b>{page}</b> of <b>{past?.pageTotal}</b> of a listing
          of <b>{past?.postTotal}</b> past{" "}
          <b>
            <SafeHtmlSpan html={category} />
          </b>{" "}
          exhibitions.
        </ListingInfo>
      )}

      {page === 1 && current?.data?.length > 0 && (
        <VerticalSlider
          spaceHeight={{
            base: [1, 2, 8],
            mobile: [1, 2, 8],
            tablet: [2, 3, 8],
            desktop: [2, 3, 8],
            screen: [2, 3, 8],
          }}
          header={"Current Exhibitions"}
          hasNav
        >
          {current?.data?.map((item: any, index: number) => (
            <VerticalSliderItemWithTeaser
              item={item}
              key={`home-upcoming-${index + 1}`}
            />
          ))}
        </VerticalSlider>
      )}

      {page === 1 && upcoming?.data?.length > 0 && (
        <VerticalSlider
          spaceHeight={{
            base: [1, 2, 8],
            mobile: [1, 2, 8],
            tablet: [2, 3, 8],
            desktop: [2, 3, 8],
            screen: [2, 3, 8],
          }}
          header={"Upcoming Exhibitions"}
          hasNav
        >
          {upcoming?.data?.map((item: any, index: number) => (
            <VerticalSliderItemWithTeaser
              item={item}
              key={`upcoming-${index + 1}`}
            />
          ))}
        </VerticalSlider>
      )}

      {page === 1 && currentEvents?.data?.length > 0 && (
        <>
          <WhatsOnListing
            keyPrefix="currentEvents"
            items={sortEventsByDateAndTime(currentEvents?.data)}
            heading={
              <PageMargins
                spaceTop={
                  !current?.data?.length && !upcoming?.data?.length
                    ? 6
                    : undefined
                }
              >
                <Heading heading="h3" asTag space={8}>
                  Events
                </Heading>
              </PageMargins>
            }
          />
        </>
      )}

      {past?.data?.length > 0 && (
        <WhatsOnListing
          keyPrefix="past"
          items={past?.data}
          heading={
            <PageMargins spaceTop={page > 1 ? 6 : undefined}>
              <Heading heading="h3" asTag space={8}>
                Past Exhibitions
              </Heading>
            </PageMargins>
          }
          pagination={
            past?.postTotal &&
            past?.pageTotal && (
              <PageMargins>
                <Pagination
                  firstPageShowMore="Show more exhibitions"
                  mobileTopSpacing
                  {...{
                    page,
                    postTotal: past?.postTotal,
                    pageTotal:
                      past?.postTotal > 10
                        ? past?.pageTotal + 1
                        : past?.pageTotal,
                  }}
                  paginationBaseUrl={`${config.baseUrl}/whats-on/${category}/`}
                />
              </PageMargins>
            )
          }
        />
      )}
    </>
  );
};

export const getStaticPaths: GetStaticPaths = () => ({
  paths: ["/whats-on/international", "/whats-on/off-site"],
  fallback: "blocking",
});

export const getStaticProps: GetStaticProps = async (context) => {
  const token = (context?.previewData as any)?.token;

  const category = context?.params?.category
    ? Array.isArray(context?.params?.category) &&
      context?.params?.category?.length
      ? (context?.params?.category[0] as string)
      : context?.params?.category
    : "not-found";

  const page = context?.params?.category
    ? Array.isArray(context?.params?.category) &&
      context?.params?.category?.length > 1
      ? parseInt(
          context?.params?.category[
            context?.params?.category.length - 1
          ] as string
        )
      : 1
    : 1;

  const currentPage = await restApiGetPostBySlugOrFallbackId(
    "page",
    category,
    token
  );

  let today = new Date().toISOString().split("T")?.[0];

  const upcoming = await restApiESQuery({
    type: "exhibition",
    perPage: 100,
    orderby: "meta.date_begin.date",
    order: "asc",
    dateBegin: today,
    dateBeginCompare: "gte",
    programmeCategorySlug: category,
  });

  const current = await restApiESQuery({
    type: "exhibition",
    perPage: 100,
    orderby: "meta.date_begin.date",
    order: "asc",
    dateBegin: today,
    dateBeginCompare: "lte",
    dateEnd: today,
    dateEndCompare: "gte",
    programmeCategorySlug: category,
  });

  const currentEvents = await restApiESQuery({
    type: "event",
    perPage: 100,
    orderby: "meta.date_begin.date",
    order: "asc",
    dateEnd: today,
    dateEndCompare: "gte",
    programmeCategorySlug: category,
  });

  const past = await restApiESQuery({
    type: "exhibition",
    perPage: page === 1 ? 10 : appConfig.postsPerPage,
    offset: page > 1 ? (page - 2) * appConfig.postsPerPage + 10 : undefined,
    page,
    orderby: "meta.date_end.date",
    order: "desc",
    dateEnd: today,
    dateEndCompare: "lt",
    programmeCategorySlug: category,
  });

  const frontendSettings = await restApiESGetSettings(["programmeCategory"]);

  if (!upcoming?.data && !current?.data && !currentEvents?.data && !past?.data)
    return {
      props: {
        frontendSettings,
      },
      notFound: true,
      revalidate: frontendSettings?.options?.copyrightNotice ? 240 : 15,
    };

  return {
    props: {
      category,
      frontendSettings,
      upcoming,
      current,
      currentEvents,
      currentPage,
      past,
      page,
    },
    revalidate: frontendSettings?.options?.copyrightNotice
      ? appConfig.revalidateInterval("exhibitionListing")
      : 15,
  };
};

WhatsOnCategoryList.getLayout = function getLayout(page: ReactElement) {
  return <LayoutMain>{page}</LayoutMain>;
};
export default WhatsOnCategoryList;
